/**
 * CookieConsentBanner.jsx
 *
 * GDPR / CCPA-compliant cookie consent banner for Angle!
 *
 * Cookie Audit — cookies & storage used by this application:
 *
 * STRICTLY NECESSARY (always active, no consent required):
 *   • sb-<project>-auth-token  – Supabase JWT session token (localStorage)
 *     Set by: @supabase/supabase-js · Purpose: keeps users logged in
 *   • sb-<project>-auth-token-code-verifier – PKCE verifier (localStorage)
 *     Set by: @supabase/supabase-js · Purpose: OAuth PKCE flow security
 *
 * FUNCTIONAL (session-scoped, cleared on tab close):
 *   • checkoutSelectedPackage   – Selected credit package during checkout (sessionStorage)
 *   • lastSelectedPackageId     – Last chosen package ID (sessionStorage)
 *   • recentlyPurchasedPackageId:<userId> – Post-purchase confirmation (sessionStorage)
 *
 * ANALYTICS (requires consent):
 *   • _ga, _ga_<ID>  – Google Analytics 4 visitor/session identifiers (cookie, 2 years)
 *     Set by: Google Analytics · Purpose: usage analytics
 *   • rocket_analytics – Rocket.new platform analytics (script in index.html)
 *     Set by: static.rocket.new · Purpose: platform telemetry
 *
 * PAYMENT (third-party, requires consent):
 *   • __stripe_mid, __stripe_sid – Stripe fraud-prevention identifiers (cookie)
 *     Set by: Stripe.js · Purpose: payment fraud detection
 *
 * RECOMMENDED ADDITIONS (not yet implemented):
 *   • cookie_consent – Store user's consent choice (localStorage, 1 year)  ← this component sets it
 *   • Google Analytics 4 (VITE_GA_MEASUREMENT_ID env var already present)
 *   • Hotjar / Microsoft Clarity for UX heatmaps (analytics tier)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie, ChevronDown, ChevronUp, Shield, BarChart2, CreditCard } from 'lucide-react';
import Icon from './AppIcon';


const CONSENT_KEY = 'angle_cookie_consent';
const CONSENT_VERSION = '1.0';

const defaultPreferences = {
  necessary: true,   // always true, cannot be toggled
  functional: true,
  analytics: false,
  payment: true,     // required for Stripe to work
};

const cookieCategories = [
  {
    id: 'necessary',
    label: 'Strictly Necessary',
    icon: Shield,
    required: true,
    description:
      'Essential for the website to function. Includes Supabase authentication tokens that keep you logged in. Cannot be disabled.',
    cookies: [
      { name: 'sb-*-auth-token', provider: 'Supabase', duration: 'Session / 1 hour (auto-refreshed)', purpose: 'Keeps you authenticated' },
      { name: 'sb-*-auth-token-code-verifier', provider: 'Supabase', duration: 'Session', purpose: 'OAuth PKCE security flow' },
    ],
  },
  {
    id: 'functional',
    label: 'Functional',
    icon: Cookie,
    required: false,
    description:
      'Remember your in-app choices such as the credit package you selected during checkout. Stored only for the current browser session.',
    cookies: [
      { name: 'checkoutSelectedPackage', provider: 'Angle!', duration: 'Session', purpose: 'Remembers selected credit package' },
      { name: 'lastSelectedPackageId', provider: 'Angle!', duration: 'Session', purpose: 'Restores last package selection' },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart2,
    required: false,
    description:
      'Help us understand how visitors use Angle! so we can improve the experience. Data is aggregated and anonymous.',
    cookies: [
      { name: '_ga, _ga_*', provider: 'Google Analytics 4', duration: '2 years', purpose: 'Visitor & session tracking' },
      { name: 'rocket_analytics', provider: 'Rocket.new', duration: 'Session', purpose: 'Platform telemetry' },
    ],
  },
  {
    id: 'payment',
    label: 'Payment & Security',
    icon: CreditCard,
    required: true,
    description:
      'Set by Stripe to detect and prevent fraudulent transactions. Required for credit purchases to work.',
    cookies: [
      { name: '__stripe_mid', provider: 'Stripe', duration: '1 year', purpose: 'Fraud prevention identifier' },
      { name: '__stripe_sid', provider: 'Stripe', duration: '30 minutes', purpose: 'Session fraud prevention' },
    ],
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function loadConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(preferences) {
  localStorage.setItem(
    CONSENT_KEY,
    JSON.stringify({ version: CONSENT_VERSION, preferences, timestamp: Date.now() })
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 focus:ring-offset-gray-900
      ${checked ? 'bg-pink-500' : 'bg-gray-600'}
      ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
        ${checked ? 'translate-x-6' : 'translate-x-1'}`}
    />
  </button>
);

const CategoryRow = ({ category, enabled, onChange }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = category?.icon;

  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800">
        <div className="flex items-center gap-3">
          {Icon && <Icon size={16} className="text-pink-400 flex-shrink-0" />}
          <div>
            <p className="text-sm font-medium text-white">{category?.label}</p>
            {category?.required && (
              <span className="text-xs text-gray-400">Always active</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Toggle
            checked={enabled}
            onChange={onChange}
            disabled={category?.required}
          />
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 py-3 bg-gray-900 border-t border-gray-700">
              <p className="text-xs text-gray-400 mb-3">{category?.description}</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-gray-300">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-700">
                      <th className="text-left pb-1 pr-3">Cookie</th>
                      <th className="text-left pb-1 pr-3">Provider</th>
                      <th className="text-left pb-1 pr-3">Duration</th>
                      <th className="text-left pb-1">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    {category?.cookies?.map((c, i) => (
                      <tr key={i} className="border-b border-gray-800 last:border-0">
                        <td className="py-1 pr-3 font-mono text-pink-300">{c?.name}</td>
                        <td className="py-1 pr-3">{c?.provider}</td>
                        <td className="py-1 pr-3 whitespace-nowrap">{c?.duration}</td>
                        <td className="py-1">{c?.purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Main Banner ──────────────────────────────────────────────────────────────

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);

  useEffect(() => {
    const saved = loadConsent();
    if (!saved) {
      // Small delay so the page loads first
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const handleAcceptAll = () => {
    const all = { necessary: true, functional: true, analytics: true, payment: true };
    saveConsent(all);
    setVisible(false);
  };

  const handleRejectNonEssential = () => {
    const essential = { necessary: true, functional: false, analytics: false, payment: true };
    saveConsent(essential);
    setVisible(false);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    setVisible(false);
  };

  const toggleCategory = (id) => {
    setPreferences(prev => ({ ...prev, [id]: !prev?.[id] }));
  };

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Backdrop (only when details panel is open) */}
          {showDetails && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-[9998]"
              onClick={() => setShowDetails(false)}
            />
          )}

          {/* Banner / Detail Panel */}
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className={`fixed z-[9999] bg-gray-900 border border-gray-700 shadow-2xl
              ${showDetails
                ? 'inset-x-4 bottom-4 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto'
                : 'inset-x-4 bottom-4 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-xl rounded-2xl'
              }`}
          >
            {/* Header */}
            <div className="flex items-start justify-between p-5 pb-3">
              <div className="flex items-center gap-2">
                <Cookie size={20} className="text-pink-400 flex-shrink-0" />
                <h2 className="text-base font-semibold text-white">Cookie Preferences</h2>
              </div>
              <button
                type="button"
                onClick={handleRejectNonEssential}
                className="text-gray-400 hover:text-white transition-colors ml-2 flex-shrink-0"
                aria-label="Close and reject non-essential cookies"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 pb-2">
              {!showDetails ? (
                <p className="text-sm text-gray-300 leading-relaxed">
                  We use cookies to keep you signed in, remember your checkout selections, and (with your consent) analyse how people use Angle! so we can improve it.{' '}
                  <button
                    type="button"
                    onClick={() => setShowDetails(true)}
                    className="text-pink-400 hover:text-pink-300 underline underline-offset-2 transition-colors"
                  >
                    Manage preferences
                  </button>
                </p>
              ) : (
                <div className="space-y-3 mt-1">
                  <p className="text-sm text-gray-400">
                    Choose which cookies you allow. Strictly necessary and payment cookies are always active.
                  </p>
                  {cookieCategories?.map(cat => (
                    <CategoryRow
                      key={cat?.id}
                      category={cat}
                      enabled={preferences?.[cat?.id]}
                      onChange={() => toggleCategory(cat?.id)}
                    />
                  ))}
                  <p className="text-xs text-gray-500 pt-1">
                    Read our{' '}
                    <a href="/privacy-policy" className="text-pink-400 hover:text-pink-300 underline underline-offset-2">
                      Privacy Policy
                    </a>{' '}
                    for full details on how we handle your data.
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2 p-5 pt-4">
              {showDetails ? (
                <>
                  <button
                    type="button"
                    onClick={handleSavePreferences}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-pink-500 hover:bg-pink-600 text-white text-sm font-medium transition-colors"
                  >
                    Save My Preferences
                  </button>
                  <button
                    type="button"
                    onClick={handleAcceptAll}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium transition-colors"
                  >
                    Accept All
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleAcceptAll}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-pink-500 hover:bg-pink-600 text-white text-sm font-medium transition-colors"
                  >
                    Accept All
                  </button>
                  <button
                    type="button"
                    onClick={handleRejectNonEssential}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium transition-colors"
                  >
                    Reject Non-Essential
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
